
//@ts-nocheck
import { Essence, EssenceCreateInput, EssenceUpdateInput } from '@repo/types';
import { EspeceSelect } from '@repo/ui/features/especes';
import { Alert } from '@repo/ui/components/ui/alert';
import { Button } from '@repo/ui/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@repo/ui/components/ui/card';
import { Form, FormControl, FormField as ShadcnFormField, FormItem, FormLabel, FormMessage } from '@repo/ui/components/ui/form';
import { Input } from '@repo/ui/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@repo/ui/components/ui/select';
import { Textarea } from '@repo/ui/components/ui/textarea';
import { FormField } from '@repo/ui/features/inventaires/shared/components/form-field';
import { ImageUpload } from '@repo/ui/features/inventaires/shared/components/image-upload';
import { FungusSelect } from '@repo/ui/features/inventaires/arbre/edit/components/fungus-select';
import { ParasiteSelect } from '@repo/ui/features/inventaires/arbre/edit/components/parasite-select';
import { useInventoryData } from '@repo/ui/hooks/use-inventory-data';
import { services } from '@repo/ui/lib/api/services/index';
import { fieldsWoodarea } from '@repo/field-configs';
import { imageService } from '@repo/ui/lib/services/image.service';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertCircle, ChevronRight, Hash, Home, Ruler, Tag, TreePine } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { getWoodareaFieldConfig, getServerFieldName, mapWoodareaFieldToField } from './utils/woodarea-field-mapper';
import { useEssenceImageOperations } from '@repo/ui/features/inventaires/hooks/use-essence-images';
import { isNotNullOrEmpty } from '../../lib/utils';


// Define form values based on woodarea fields - using underscore to match server schema
type FormValues = {
  // Basic fields
  espece_id: string;
  diametre: number | '';
  hauteur: number | '';
  count_subject: number | '';

  // Woodarea specific fields
  stade_dev: string;
  port_arbre: string;
  port_arbre_other?: string;
  critere: string | string[]; // Can be string or array depending on data
  critere_other?: string;
  etat_san_general: string; // Changed from JSON to String
  risque: string;
  nbre_sujet_concerne: number | '';
  travaux: string[];
  travaux_aucun?: string;
  travaux_soin?: string;
  travaux_other?: string;
  travaux_abattage?: string;
  travaux_demontage?: string;
  travaux_mise_en_securite?: string;
  travaux_taille_sanitaire?: string;
  travaux_dechiffrage?: string;
  travaux_couple_selective?: string;
  date_travaux?: string;
  user_edited_date_travaux?: string;
  date_pro_visite?: string;
  user_edited_date_pro_visite?: string;
  travaux_com?: string;
  abattage?: string;
};

function formatDateInputValue(value?: string | Date | null): string {
  if (!value) {
    return '';
  }
  const date = typeof value === 'string' ? new Date(value) : value;
  if (!date || Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toISOString().slice(0, 10);
}

function computeUserEditedDateFromPreset(preset?: string | null): string {
  if (!preset) {
    return '';
  }

  const offsetDate = new Date();
  switch (preset) {
    case 'immediat':
      offsetDate.setDate(offsetDate.getDate() + 7); // 1 week from now
      break;
    case '1an':
      offsetDate.setFullYear(offsetDate.getFullYear() + 1);
      break;
    case '2ans':
      offsetDate.setFullYear(offsetDate.getFullYear() + 2);
      break;
    case '3ans':
      offsetDate.setFullYear(offsetDate.getFullYear() + 3);
      break;
    case '5ans':
      offsetDate.setFullYear(offsetDate.getFullYear() + 5);
      break;
    default:
      return '';
  }

  return formatDateInputValue(offsetDate);
}

// Calculate échéance based on a given date (inverse function)
function calculateEcheanceFromDate(dateString: string): string {
  if (!dateString) return '';

  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time to start of day
  const targetDate = new Date(dateString);
  targetDate.setHours(0, 0, 0, 0);

  // Calculate difference in days
  const diffTime = targetDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  // Calculate difference in months
  const diffMonths = (targetDate.getFullYear() - today.getFullYear()) * 12 + (targetDate.getMonth() - today.getMonth());

  // Determine échéance based on date
  if (diffDays <= 7) {
    return 'immediat';
  } else if (diffMonths < 12) {
    return '1an';
  } else if (diffMonths < 24) {
    return '2ans';
  } else if (diffMonths < 36) {
    return '3ans';
  } else {
    return '5ans';
  }
}

interface SelectOption<T = any> {
  value: T;
  label: string;
}

interface EssenceFormProps {
  epaysageId: number;
  essence?: Essence;
  mode: 'create' | 'edit';
  onSuccess: (essence?: Essence) => void;
  onCancel: () => void;
}

export function EssenceForm({ epaysageId, essence, mode, onSuccess, onCancel }: EssenceFormProps) {
  const [error, setError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const essenceId = essence?.id;
  const imageOperations = useEssenceImageOperations();
  const imageUploadConfig = useMemo(
    () => ({
      maxFiles: 1,
      acceptedTypes: ['image/jpeg', 'image/png', 'image/webp'],
      maxSize: 20,
    }),
    []
  );



  // Load inventory data (champignons, nuisibles) for freeforms
  const {
    champignons,
    nuisibles,
    isLoading: isLoadingInventoryData,
    hasError: hasInventoryDataError,
  } = useInventoryData(true);

  const form = useForm<FormValues>({
    defaultValues: {
      espece_id: essence?.espece_id?.toString() || '',
      diametre: essence?.diametre || '',
      hauteur: essence?.hauteur || '',
      count_subject: essence?.count_subject || '',
      stade_dev: essence?.stade_dev || '',
      port_arbre: essence?.port_arbre || '',
      port_arbre_other: essence?.port_arbre_other || '',
      critere: typeof essence?.critere === 'string'
        ? essence.critere
        : (Array.isArray(essence?.critere) && essence.critere.length > 0
          ? essence.critere[0] as string
          : ''),
      critere_other: essence?.critere_other || '',
      etat_san_general: essence?.etat_san_general || '',
      risque: essence?.risque || '',
      nbre_sujet_concerne: essence?.nbre_sujet_concerne || '',
      travaux: Array.isArray(essence?.travaux) ? essence.travaux as string[] : [],
      travaux_aucun: essence?.travaux_aucun || '',
      travaux_soin: essence?.travaux_soin || '',
      travaux_other: essence?.travaux_other || '',
      travaux_abattage: essence?.travaux_abattage || '',
      travaux_demontage: essence?.travaux_demontage || '',
      travaux_mise_en_securite: essence?.travaux_mise_en_securite || '',
      travaux_taille_sanitaire: essence?.travaux_taille_sanitaire || '',
      travaux_dechiffrage: essence?.travaux_dechiffrage || '',
      travaux_couple_selective: essence?.travaux_couple_selective || '',
      date_travaux: essence?.date_travaux || '',
      user_edited_date_travaux: formatDateInputValue(essence?.user_edited_date_travaux),
      date_pro_visite: essence?.date_pro_visite || '',
      user_edited_date_pro_visite: formatDateInputValue(essence?.user_edited_date_pro_visite),
      travaux_com: essence?.travaux_com || '',
      abattage: essence?.abattage || '',
    },
  });

  // Watch all form values at once to avoid multiple subscriptions
  const watchedValues = useWatch({
    control: form.control,
    defaultValue: form.getValues(),
  });

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      console.log("Form values:", values);

      if (!values.espece_id) {
        form.setError('espece_id', { message: 'Sélectionnez une espèce' });
        throw new Error('Espèce requise');
      }

      const payload = {
        espece_id: Number(values.espece_id),
        diametre: values.diametre === '' ? null : Number(values.diametre),
        hauteur: values.hauteur === '' ? null : Number(values.hauteur),
        count_subject: values.count_subject === '' ? null : Number(values.count_subject),
        stade_dev: values.stade_dev || null,
        port_arbre: values.port_arbre || null,
        port_arbre_other: values.port_arbre_other || null,
        critere: values.critere || null,
        critere_other: values.critere_other || null,
        etat_san_general: values.etat_san_general || null,
        risque: values.risque || null,
        nbre_sujet_concerne: values.nbre_sujet_concerne === '' ? null : Number(values.nbre_sujet_concerne),
        travaux: values.travaux && values.travaux.length > 0 ? values.travaux : null,
        travaux_soin: values.travaux_soin || null,
        travaux_other: values.travaux_other || null,
        // travaux_aucun: String(values.travaux_aucun) || null,
        travaux_abattage: isNotNullOrEmpty(values.travaux_abattage) ? String(values.travaux_abattage) : null,
        travaux_demontage: isNotNullOrEmpty(values.travaux_demontage) ? String(values.travaux_demontage) : null,
        travaux_mise_en_securite: isNotNullOrEmpty(values.travaux_mise_en_securite) ? String(values.travaux_mise_en_securite) : null,
        travaux_taille_sanitaire: isNotNullOrEmpty(values.travaux_taille_sanitaire) ? String(values.travaux_taille_sanitaire) : null,
        travaux_dechiffrage: isNotNullOrEmpty(values.travaux_dechiffrage) ? String(values.travaux_dechiffrage) : null,
        travaux_couple_selective: isNotNullOrEmpty(values.travaux_couple_selective) ? String(values.travaux_couple_selective) : null,
        date_travaux: values.date_travaux || null,
        user_edited_date_travaux: values.user_edited_date_travaux ? new Date(values.user_edited_date_travaux) : null,
        date_pro_visite: values.date_pro_visite || null,
        user_edited_date_pro_visite: values.user_edited_date_pro_visite ? new Date(values.user_edited_date_pro_visite) : null,
        travaux_com: values.travaux_com || null,
        abattage: values.abattage || null,
        epaysage_id: epaysageId,
        status_travaux: false,
      };
      console.log("Payload:", payload);

      if (mode === 'create') {
        return services.essence.createEssence(epaysageId, payload as unknown as EssenceCreateInput);
      } else {
        const updatePayload = { id: essence!.id!, ...payload };
        return services.essence.updateEssence(epaysageId, essence!.id!, updatePayload as unknown as EssenceUpdateInput);
      }
    },
    onSuccess: (response) => {
      setError(null);
      if (mode === 'create') {
        toast.success('Essence ajoutée avec succès');
      } else {
        toast.success('Essence modifiée avec succès');
      }
      onSuccess(response.data);
    },
    onError: (error: any) => {
      console.error('Erreur lors de la sauvegarde:', error);

      // Handle validation errors from backend
      if (error?.response?.data?.errors) {
        const backendErrors = error.response.data.errors;
        Object.entries(backendErrors).forEach(([field, message]) => {
          if (field !== '_form') {
            form.setError(field as keyof FormValues, { message: message as string });
          }
        });

        if (backendErrors._form) {
          setError(backendErrors._form as string);
        }
      } else {
        setError(error?.response?.data?.message || error?.message || 'Erreur lors de la sauvegarde de l\'essence');
      }
    },
  });

  const handleSubmit = (values: FormValues) => {
    setError(null);
    console.log('Submitting form with values:', values);
    mutation.mutate(values);
  };

  const handleImageChange = useCallback(
    async (fieldName: string, value: string | File) => {
      if (!essenceId) {
        return;
      }
      const field = fieldName as 'img1' | 'img2';
      try {
        if (value instanceof File) {
          await imageOperations.upload.mutateAsync({
            epaysageId,
            essenceId,
            field,
            file: value,
          });
        } else if (value === '') {
          await imageOperations.delete.mutateAsync({
            epaysageId,
            essenceId,
            field,
          });
        }
      } catch (imageError) {
        console.error(`Erreur lors de la mise à jour de ${field}`, imageError);
      }
    },
    [epaysageId, essenceId, imageOperations.upload, imageOperations.delete]
  );

  const handleImageRotate = useCallback(
    async (fieldName: string, rotation: number) => {
      if (!essenceId) {
        return;
      }
      const field = fieldName as 'img1' | 'img2';
      try {
        await imageOperations.rotate.mutateAsync({
          epaysageId,
          essenceId,
          field,
          rotation,
        });
      } catch (imageError) {
        console.error(`Erreur lors de la rotation de ${field}`, imageError);
        throw imageError;
      }
    },
    [epaysageId, essenceId, imageOperations.rotate]
  );

  const resolveImageValue = useCallback(
    (value?: string | null) => (value ? imageService.getImageUrl(value, 'essence') : ''),
    []
  );

  // Helper function to get field options from woodarea config
  const getFieldOptions = (fieldName: string): SelectOption[] => {
    const field = fieldsWoodarea[fieldName as keyof typeof fieldsWoodarea] as any;
    if (field?.values) {
      return field.values.map((value: any) => ({
        value: value.id,
        label: value.label,
      }));
    }
    return [];
  };

  // Render freeforms function similar to arbre form
  const renderFreeforms = useCallback((fieldName: string) => {
    const woodareaField = getWoodareaFieldConfig(fieldName);
    if (!woodareaField?.freeforms) return null;

    // Get current field values to check if freeform options are selected
    // Use getValues() instead of watch() to avoid subscription loops
    const serverFieldName = getServerFieldName(fieldName);
    const currentValue = form.getValues(serverFieldName as keyof FormValues);
    const currentValues = Array.isArray(currentValue)
      ? currentValue
      : [currentValue].filter(Boolean);

    return (
      <div className="space-y-3">
        {woodareaField.freeforms.map((ff: any) => {
          const ffServerKey = ff.server || `${fieldName}_${ff.id}`;
          const ffValue = form.getValues(ffServerKey as keyof FormValues) || '';
          const ffType = (ff.type || 'String').toLowerCase();
          const label = ff.label || 'Autre, précisez :';

          // Show freeform input if the corresponding option is selected OR if it already has a value
          const isSelected = Array.isArray(currentValues) ? currentValues.includes(ff.id) : currentValues === ff.id;
          const hasValue = ffValue && (typeof ffValue === 'string' ? ffValue.trim() !== '' : Array.isArray(ffValue) ? ffValue.length > 0 : true);
          if (!isSelected && !hasValue) return null;

          if (ffType === 'integer') {
            return (
              <div key={ff.id} className="space-y-2">
                <ShadcnFormField
                  control={form.control}
                  name={ffServerKey as keyof FormValues}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium">{label}</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          value={field.value as number | ''}
                          onChange={(e) => field.onChange(e.target.value === '' ? '' : Number(e.target.value))}
                          placeholder="Entrer un nombre"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            );
          }

          if (ffType === 'array' && ff.constraint === 'Fungus') {
            const rawValue = ffValue as any;
            const fungusValue: number[] = Array.isArray(rawValue)
              ? rawValue.map((v: any) => typeof v === 'number' ? v : Number(v)).filter((v: number) => !isNaN(v))
              : [];
            return (
              <FungusSelect
                key={ff.id}
                champignons={champignons || []}
                value={fungusValue}
                onChange={(val: number[]) => form.setValue(ffServerKey as keyof FormValues, val as any, {
                  shouldDirty: true,
                  shouldTouch: false,
                  shouldValidate: false,
                })}
                label={label}
                fieldId={`${fieldName}-${ff.id}`}
                placeholder="Sélectionnez des champignons..."
              />
            );
          }

          if (ffType === 'array' && ff.constraint === 'Parasites') {
            const rawValue = ffValue as any;
            const parasiteValue: number[] = Array.isArray(rawValue)
              ? rawValue.map((v: any) => typeof v === 'number' ? v : Number(v)).filter((v: number) => !isNaN(v))
              : [];
            return (
              <ParasiteSelect
                key={ff.id}
                nuisibles={nuisibles || []}
                value={parasiteValue}
                onChange={(val: number[]) => form.setValue(ffServerKey as keyof FormValues, val as any, {
                  shouldDirty: true,
                  shouldTouch: false,
                  shouldValidate: false,
                })}
                label={label}
                fieldId={`${fieldName}-${ff.id}`}
                placeholder="Sélectionnez des nuisibles..."
              />
            );
          }

          return (
            <div key={ff.id} className="space-y-2">
              <ShadcnFormField
                control={form.control}
                name={ffServerKey as keyof FormValues}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">{label}</FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        value={field.value as string}
                        onChange={(e) => field.onChange(e.target.value)}
                        placeholder={ffType === 'array' ? 'Entrez des valeurs séparées par des virgules' : 'Saisir ici'}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          );
        })}
      </div>
    );
  }, [form, champignons, nuisibles]);

  // Helper function to map form field name to woodarea field name


  // Handle field change for FormField component
  const handleFieldChange = useCallback((fieldName: string, value: any) => {
    // fieldName is the woodarea field name (e.g., 'caracteristiques-de-lespace-boise')
    // We need to map it to the server field name (e.g., 'caract')


    const serverFieldName = getServerFieldName(fieldName);
    console.log('serverFieldName', serverFieldName);
    console.log('value', value);
    // Update the form value without triggering validation or touch to avoid infinite loops
    form.setValue(serverFieldName as keyof FormValues, value as any, {
      shouldDirty: true,
      shouldTouch: false,
      shouldValidate: false,
    });

    if (fieldName === 'preco-travaux-date-travaux') {
      const computedDate = computeUserEditedDateFromPreset(typeof value === 'string' ? value : undefined);
      form.setValue('user_edited_date_travaux', computedDate, {
        shouldDirty: true,
        shouldTouch: false,
        shouldValidate: false,
      });
    }

    if (fieldName === 'preco-travaux-prochaine-visite') {
      const computedDate = computeUserEditedDateFromPreset(typeof value === 'string' ? value : undefined);
      form.setValue('user_edited_date_pro_visite', computedDate, {
        shouldDirty: true,
        shouldTouch: false,
        shouldValidate: false,
      });
    }

    // Clear error when user starts typing (only if error exists)
    setFormErrors(prev => {
      if (prev[fieldName]) {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      }
      return prev;
    });
  }, [form]);

  return (
    <div className="w-full max-w-6xl mx-auto p-6">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <TreePine className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">
                  {mode === 'create' ? 'Nouvelle essence' : 'Modifier l\'essence'}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {mode === 'create'
                    ? 'Ajoutez une nouvelle essence à cet espace boisé'
                    : 'Modifiez les caractéristiques de cette essence'
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <div>
                <h4 className="font-medium">Erreur</h4>
                <p className="text-sm">{error}</p>
              </div>
            </Alert>
          )}

          {/* Inventory Data Loading Alert */}
          {isLoadingInventoryData && (
            <Alert className="border-blue-200 bg-blue-50">
              <AlertCircle className="h-4 w-4 text-blue-600" />
              <div className="text-blue-800">
                Chargement des données d'inventaire (champignons, nuisibles)...
              </div>
            </Alert>
          )}

          {/* Inventory Data Error Alert */}
          {hasInventoryDataError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <div>
                <h4 className="font-medium">Erreur</h4>
                <p className="text-sm">Erreur lors du chargement des données d'inventaire</p>
              </div>
            </Alert>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-6">
              {/* Basic Information */}
              <Card>
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Home className="h-5 w-5 text-primary" />
                    Informations de base
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ShadcnFormField
                    control={form.control}
                    name="espece_id"
                    render={({ field }: { field: any }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2">
                          <TreePine className="h-4 w-4" />
                          Espèce *
                        </FormLabel>
                        <FormControl>
                          <EspeceSelect
                            value={field.value || undefined}
                            onChange={(value) => field.onChange(value || '')}
                            searchMode="server"
                            fetchFilters={{ limit: 1000, sortBy: 'alphanum' }}
                            placeholder="Rechercher une espèce..."
                            emptyMessage="Aucune espèce trouvée"
                            variant="form"
                            className="react-select-container"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-2 gap-4">
                    {(() => {
                      const field = mapWoodareaFieldToField('diametre');
                      if (!field) return null;
                      return (
                        <FormField
                          key="diametre"
                          field={field}
                          value={watchedValues.diametre || ''}
                          error={form.formState.errors.diametre?.message}
                          onChange={handleFieldChange}
                          renderFreeforms={renderFreeforms}
                          formType="essence"
                        />
                      );
                    })()}

                    {(() => {
                      const field = mapWoodareaFieldToField('hauteur');
                      if (!field) return null;
                      return (
                        <FormField
                          key="hauteur"
                          field={field}
                          value={watchedValues.hauteur || ''}
                          error={form.formState.errors.hauteur?.message}
                          onChange={handleFieldChange}
                          renderFreeforms={renderFreeforms}
                          formType="essence"
                        />
                      );
                    })()}
                  </div>



                  {(() => {
                    const field = mapWoodareaFieldToField('countSubject');
                    if (!field) return null;
                    return (
                      <FormField
                        key="count_subject"
                        field={field}
                        value={watchedValues.count_subject || ''}
                        error={form.formState.errors.count_subject?.message}
                        onChange={handleFieldChange}
                        renderFreeforms={renderFreeforms}
                        formType="essence"
                      />
                    );
                  })()}
                </CardContent>
              </Card>

              {/* Characteristics */}
              <Card>
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg">Caractéristiques de l'espace boisé</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {(() => {
                    const field = mapWoodareaFieldToField('port-de-larbre');
                    if (!field) return null;
                    return (
                      <FormField
                        key="port_arbre"
                        field={field}
                        value={watchedValues.port_arbre || ''}
                        error={form.formState.errors.port_arbre?.message}
                        onChange={handleFieldChange}
                        renderFreeforms={renderFreeforms}
                        formType="essence"
                      />
                    );
                  })()}
                  {(() => {
                    const field = mapWoodareaFieldToField('critere-remarquable');
                    if (!field) return null;
                    return (
                      <FormField
                        key="critere"
                        field={field}
                        value={watchedValues.critere || ''}
                        error={form.formState.errors.critere?.message}
                        onChange={handleFieldChange}
                        renderFreeforms={renderFreeforms}
                        formType="essence"
                      />
                    );
                  })()}
                </CardContent>
              </Card>


            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Health Status */}
              <Card>
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg">Diagnostic sanitaire</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {(() => {
                    const field = mapWoodareaFieldToField('stade-de-developpement');
                    if (!field) return null;
                    return (
                      <FormField
                        key="stade_dev"
                        field={field}
                        value={watchedValues.stade_dev || ''}
                        error={form.formState.errors.stade_dev?.message}
                        onChange={handleFieldChange}
                        renderFreeforms={renderFreeforms}
                        formType="essence"
                      />
                    );
                  })()}
                  {(() => {
                    const field = mapWoodareaFieldToField('etat-sanitaire-general');
                    if (!field) return null;
                    return (
                      <FormField
                        key="etat_san_general"
                        field={field}
                        value={watchedValues.etat_san_general || ''}
                        error={form.formState.errors.etat_san_general?.message}
                        onChange={handleFieldChange}
                        renderFreeforms={renderFreeforms}
                        formType="essence"
                      />
                    );
                  })()}
                  {(() => {
                    const field = mapWoodareaFieldToField('risque-de-rupture');
                    if (!field) return null;
                    return (
                      <FormField
                        key="risque"
                        field={field}
                        value={watchedValues.risque || ''}
                        error={form.formState.errors.risque?.message}
                        onChange={handleFieldChange}
                        renderFreeforms={renderFreeforms}
                        formType="essence"
                      />
                    );
                  })()}


                </CardContent>
              </Card>

            </div>
          </div>

          {/* Work Recommendations - Full Width */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg">Préconisation de travaux</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(() => {
                  const field = mapWoodareaFieldToField('nbre-sujet-concerne');
                  if (!field) return null;
                  return (
                    <FormField
                      key="nbre_sujet_concerne"
                      field={field}
                      value={watchedValues.nbre_sujet_concerne || ''}
                      error={form.formState.errors.nbre_sujet_concerne?.message}
                      onChange={handleFieldChange}
                      renderFreeforms={renderFreeforms}
                      formType="essence"
                    />
                  );
                })()}

                {(() => {
                  const field = mapWoodareaFieldToField('preco-travaux-date-travaux');
                  if (!field) return null;
                  return (
                    <FormField
                      key="date_travaux"
                      field={field}
                      value={watchedValues.date_travaux || ''}
                      error={form.formState.errors.date_travaux?.message}
                      onChange={handleFieldChange}
                      renderFreeforms={renderFreeforms}
                      formType="essence"
                    />
                  );
                })()}
                <ShadcnFormField
                  control={form.control}
                  name="user_edited_date_travaux"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Date saisie par l'utilisateur (travaux)</FormLabel>
                      <FormControl>
                        <Input
                          type="date"
                          value={field.value || ''}
                          onChange={(event) => {
                            const selectedDate = event.target.value;

                            // Update échéance based on selected date
                            const calculatedEcheance = calculateEcheanceFromDate(selectedDate);
                            if (calculatedEcheance) {
                              handleFieldChange('preco-travaux-date-travaux', calculatedEcheance);
                            }
                            field.onChange(selectedDate);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {(() => {
                  const field = mapWoodareaFieldToField('preco-travaux-prochaine-visite');
                  if (!field) return null;
                  return (
                    <FormField
                      key="date_pro_visite"
                      field={field}
                      value={watchedValues.date_pro_visite || ''}
                      error={form.formState.errors.date_pro_visite?.message}
                      onChange={handleFieldChange}
                      renderFreeforms={renderFreeforms}
                      formType="essence"
                    />
                  );
                })()}
                <ShadcnFormField
                  control={form.control}
                  name="user_edited_date_pro_visite"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Date saisie par l'utilisateur (prochaine visite)</FormLabel>
                      <FormControl>
                        <Input
                          type="date"
                          value={field.value || ''}
                          onChange={(event) => {
                            const selectedDate = event.target.value;

                            // Update échéance based on selected date
                            const calculatedEcheance = calculateEcheanceFromDate(selectedDate);
                            if (calculatedEcheance) {
                              handleFieldChange('preco-travaux-prochaine-visite', calculatedEcheance);
                            }
                            field.onChange(selectedDate);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {(() => {
                const field = mapWoodareaFieldToField('preconisation-de-travaux');
                if (!field) return null;

                return (
                  <FormField
                    key="travaux"
                    field={field}
                    value={watchedValues.travaux || []}
                    error={form.formState.errors.travaux?.message}
                    onChange={handleFieldChange}
                    renderFreeforms={renderFreeforms}
                    formType="essence"
                  />
                );
              })()}

              <ShadcnFormField
                control={form.control}
                name="travaux_com"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Commentaires</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Commentaires sur les travaux..."
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg">Photos de l&apos;essence</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {mode === 'edit' && essenceId ? (
                <>
                  <p className="text-sm text-muted-foreground">
                    Ajoutez des visuels pour documenter cette essence. Formats acceptés&nbsp;: JPG, PNG ou WebP (20&nbsp;MB max).
                  </p>
                  <div className="grid gap-6 md:grid-cols-2">
                    <ImageUpload
                      name="img1"
                      label="Image principale"
                      value={resolveImageValue(essence?.img1)}
                      onChange={handleImageChange}
                      onRotate={handleImageRotate}
                      imageConfig={imageUploadConfig}
                      error={imageOperations.error instanceof Error ? imageOperations.error.message : undefined}
                      isLoading={imageOperations.isLoading}
                    />
                    <ImageUpload
                      name="img2"
                      label="Image secondaire"
                      value={resolveImageValue(essence?.img2)}
                      onChange={handleImageChange}
                      onRotate={handleImageRotate}
                      imageConfig={imageUploadConfig}
                      error={imageOperations.error instanceof Error ? imageOperations.error.message : undefined}
                      isLoading={imageOperations.isLoading}
                    />
                  </div>
                </>
              ) : (
                <Alert className="border-dashed">
                  <AlertCircle className="h-4 w-4" />
                  <div>
                    <h4 className="font-medium">Images disponibles après création</h4>
                    <p className="text-sm text-muted-foreground">
                      Sauvegardez d&apos;abord l&apos;essence pour pouvoir téléverser ses photos.
                    </p>
                  </div>
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={mutation.isPending}
              className="flex-1"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={mutation.isPending}
              className="flex-1 flex items-center gap-2"
            >
              {mutation.isPending ? (
                <>
                  <div className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  Sauvegarde...
                </>
              ) : (
                <>
                  {mode === 'create' ? 'Créer l\'essence' : 'Sauvegarder'}
                  <ChevronRight className="h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
